import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ScanLine, RefreshCw, X } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useFlash } from "../lib/flash";
import PickupCard, { OPEN, orderState, UndoToast } from "../components/PickupCard";
import QrScanner, { tokenFromScan } from "../components/QrScanner";
import CustomerPanel from "../components/CustomerPanel";
import { PickupShopLabel, ElsewhereNotice } from "../components/PickupShop";
import { useGroupStores } from "../components/StoreSwitcher";
import { fmtFrom } from "../lib/format";

const TABS = [
  ["mangler", "Mangler"],
  ["udleveret", "Udleveret i dag"],
  ["alle", "Alle"],
];
const startOfToday = () => new Date().setHours(0, 0, 0, 0);
const endOfToday = () => startOfToday() + 86400000;
const byName = (a, b) => (a.name || "").localeCompare(b.name || "", "da");
const dayKey = (iso) => new Date(iso).toLocaleDateString("da-DK", { timeZone: "Europe/Copenhagen" });

function dayLabel(iso) {
  const today = dayKey(new Date().toISOString());
  const tomorrow = dayKey(new Date(Date.now() + 86400000).toISOString());
  const k = dayKey(iso);
  if (k === today) return "Senere i dag";
  if (k === tomorrow) return "I morgen";
  return new Date(iso).toLocaleDateString("da-DK", { weekday: "short", day: "numeric", month: "short", timeZone: "Europe/Copenhagen" });
}

function groups(orders, now) {
  const open = orders.filter((o) => OPEN.has(o.status));
  const overdue = open.filter((o) => orderState(o, now).overdue).sort((a, b) => Date.parse(a.pickup_end) - Date.parse(b.pickup_end));
  const nextStart = (o) => Date.parse(orderState(o, now).next?.start || o.pickup_start);
  const later = open.filter((o) => orderState(o, now).notStarted).sort((a, b) => nextStart(a) - nextStart(b) || byName(a, b));
  const ready = open.filter((o) => !overdue.includes(o) && !later.includes(o)).sort(byName);
  const sod = startOfToday();
  const picked = orders
    .filter((o) => o.status === "picked_up" && o.picked_up_at && Date.parse(o.picked_up_at) >= sod)
    .sort((a, b) => Date.parse(b.picked_up_at) - Date.parse(a.picked_up_at));
  const mangler = [...overdue, ...ready, ...later];
  const rest = orders.filter((o) => !OPEN.has(o.status) && !picked.includes(o));
  const eod = endOfToday();
  const readyToday = ready.length + later.filter((o) => nextStart(o) < eod).length;
  const sections = [];
  if (overdue.length) sections.push(["Overskredet", overdue, "overdue"]);
  if (ready.length) sections.push(["Klar nu", ready, "ready"]);
  if (later.length) {
    sections.push(["Senere", null, "later"]);
    for (const o of later) {
      const label = dayLabel(new Date(nextStart(o)).toISOString());
      const last = sections[sections.length - 1];
      if (last[2] === "day" && last[0] === label) last[1].push(o);
      else sections.push([label, [o], "day"]);
    }
  }
  return { mangler, udleveret: picked, alle: [...mangler, ...picked, ...rest], overdue: overdue.length, readyToday, sections };
}

function Summary({ g, setTab }) {
  const items = [
    ["mangler", g.mangler.length, "Mangler", ""],
    ["udleveret", g.udleveret.length, "Udleveret i dag", "green"],
    ["mangler", g.overdue, "Overskredet", g.overdue ? "red" : ""],
  ];
  return (
    <div className="pstats" data-testid="pickup-summary">
      {items.map(([tab, n, label, tone], i) => (
        <button key={label} className={`pstat ${tone}`} onClick={() => setTab(tab)} data-testid={`pickup-stat-${i}`}>
          <b>{n}</b>
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
}

export default function PickupDesk() {
  const { store } = useAuth();
  const flash = useFlash();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([id]) => id === params.get("tab")) ? params.get("tab") : "mangler";
  const setTab = (t) => setParams(t === "mangler" ? {} : { tab: t }, { replace: true });
  const [d, setD] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [q, setQ] = useState("");
  const [extra, setExtra] = useState([]);
  const [hl, setHl] = useState([]);
  const [undo, setUndo] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(null);
  const [custId, setCustId] = useState(null);
  const [elsewhere, setElsewhere] = useState(null);
  const group = useGroupStores();
  const multi = !store?.staff && !store?.is_owner && group?.stores?.length > 1;
  const shopName = store?.name;
  const searchRef = useRef();

  const load = useCallback(
    () =>
      api
        .get("/pickup")
        .then((r) => {
          setD(r.data);
          setNow(Date.now());
        })
        .catch(() => {}),
    []
  );
  useEffect(() => {
    load();
    const t = setInterval(() => document.visibilityState === "visible" && load(), 30000);
    const onVis = () => document.visibilityState === "visible" && load();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [load]);

  const term = q.trim().toLowerCase();
  const hay = (o) => `${o.code} ${o.name} ${o.title}`.toLowerCase();
  const localMatch = !!d && d.orders.some((o) => hay(o).includes(term));
  useEffect(() => {
    setHl([]);
    if (term.length < 2 || localMatch) return setExtra([]);
    const id = setTimeout(() => {
      api.get(`/pickup/search?q=${encodeURIComponent(term)}`).then((r) => setExtra(r.data.orders)).catch(() => setExtra([]));
    }, 250);
    return () => clearTimeout(id);
  }, [term, localMatch]);

  const act = async (o, action) => {
    const early = action === "pickup" ? orderState(o, Date.now()) : null;
    if (early?.notStarted && !window.confirm(`Afhentning starter ${fmtFrom(early.next.start)} – udlever alligevel?`)) return;
    try {
      let r;
      if (action === "undo-any") {
        r = await api.post(`/orders/${o.id}/revert`).catch((err) => {
          if (err.response?.status === 409) return api.post(`/orders/${o.id}/undo`);
          throw err;
        });
      } else {
        r = await api.post(`/orders/${o.id}/${action}`);
      }
      if (r.data.undoable) setUndo({ oid: o.id, kind: action, text: r.data.flash, key: Date.now() });
      else if (r.data.flash) flash(r.data.flash);
      setQ("");
      setExtra([]);
      setScanned(null);
      searchRef.current?.focus();
      load();
    } catch (err) {
      flash(apiError(err.response?.data?.detail) || err.message, "error");
    }
  };

  const revert = async () => {
    const u = undo;
    setUndo(null);
    try {
      await api.post(`/orders/${u.oid}/revert`);
      flash("Fortrudt – ordren er sat tilbage.");
      load();
    } catch (err) {
      flash(apiError(err.response?.data?.detail) || err.message, "error");
    }
  };
  const closeUndo = useCallback(() => setUndo(null), []);

  const onScan = useCallback(
    async (text) => {
      setScanning(false);
      const token = tokenFromScan(text);
      if (!token) return flash("QR-koden er ikke en JaTak-ordre.", "error");
      try {
        const r = await api.get("/pickup/lookup", { params: { token } });
        setElsewhere(null);
        setScanned(r.data.order);
      } catch (err) {
        if (err.response?.status === 409 && err.response.data?.other_store) return setElsewhere(err.response.data.other_store);
        flash(
          err.response?.status === 404 ? "Ordren findes ikke i din butik – tjek at kunden står det rigtige sted." : apiError(err.response?.data?.detail) || err.message,
          "error"
        );
      }
    },
    [flash]
  );

  if (!d)
    return (
      <>
        <div className="page-head">
          <div>
            <h1>Udlevering</h1>
          </div>
        </div>
        <div className="pstats">
          {[0, 1, 2].map((i) => (
            <div className="pstat skel" key={i}>
              <span className="sk sk-num" />
              <span className="sk sk-line" />
            </div>
          ))}
        </div>
      </>
    );

  const g = groups(d.orders, now);
  const matches = term ? d.orders.filter((o) => hay(o).includes(term)) : null;
  const matchIds = new Set((matches || []).map((o) => o.id));
  const extraVisible = term ? extra.filter((o) => !matchIds.has(o.id)) : [];
  const list = matches || g[tab];

  const onEnter = async () => {
    const all = [...(matches || []), ...extraVisible];
    const open = all.filter((o) => OPEN.has(o.status) || o.status === "no_show");
    const exact = open.filter((o) => (o.code || "").toLowerCase() === term);
    const pick = exact.length === 1 ? exact : open;
    if (pick.length === 1) return act(pick[0], "pickup");
    if (pick.length > 1) {
      setHl(pick.map((o) => o.id));
      return flash("Flere ordrer passer – tryk på den rigtige kunde.", "warn");
    }
    const other = await api.get("/pickup/elsewhere", { params: { code: term } }).then((r) => r.data.other_store).catch(() => null);
    if (other) return setElsewhere(other);
    flash(`Ingen åben ordre med «${q.trim()}». Tjek koden – eller søg på navnet.`, "error");
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Udlevering</h1>
          <p data-testid="pickup-headline">
            {g.mangler.length} mangler at hente · {g.readyToday} klar i dag · {g.udleveret.length} udleveret i dag
          </p>
        </div>
        <div className="actions">
          <button className="btn" onClick={load} data-testid="pickup-refresh">
            <RefreshCw size={16} /> Opdater
          </button>
        </div>
      </div>

      <PickupShopLabel multi={multi} />
      <ElsewhereNotice other={elsewhere} canSwitch={!store?.staff && !!group?.stores?.some((s) => s.id === elsewhere?.id)} onClose={() => setElsewhere(null)} />
      <Summary g={g} setTab={setTab} />

      <div className="pickup-search">
        <div className="ps-row">
          <input
            ref={searchRef}
            type="search"
            placeholder="Kunden siger sin kode"
            autoComplete="off"
            autoCapitalize="characters"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && term) {
                e.preventDefault();
                onEnter();
              }
            }}
            aria-label="Søg på kode eller navn"
            data-testid="pickup-search"
          />
          <button className="btn ps-scan" onClick={() => setScanning(true)} data-testid="pickup-scan-btn">
            <ScanLine size={20} /> <span>Scan</span>
          </button>
        </div>
      </div>

      {!term && (
        <div className="dash-tabs ptabs" role="tablist" data-testid="pickup-tabs">
          {TABS.map(([id, label]) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              className={"dash-tab" + (tab === id ? " active" : "")}
              onClick={() => setTab(id)}
              data-testid={`pickup-tab-${id}`}
            >
              {label} <span className="dash-count">{g[id].length}</span>
            </button>
          ))}
        </div>
      )}

      <div className="pickup-list" data-testid="pickup-list">
        {!term && tab === "mangler"
          ? g.sections.map(([label, rows, kind]) => (
              <div key={kind + label} className="pickup-section" data-testid={`pickup-section-${kind === "day" ? "day" : kind}`}>
                <p className={`pickup-divider${kind === "day" ? " sub" : ""}${kind === "overdue" ? " red" : ""}`}>
                  {label}
                  {rows && <span className="dash-count">{rows.length}</span>}
                </p>
                {rows?.map((o) => (
                  <PickupCard key={o.id} o={o} now={now} onAct={act} onCustomer={setCustId} highlight={hl.includes(o.id)} />
                ))}
              </div>
            ))
          : list.map((o) => <PickupCard key={o.id} o={o} now={now} onAct={act} onCustomer={setCustId} highlight={hl.includes(o.id)} />)}
        {extraVisible.length > 0 && (
          <>
            <p className="pickup-divider" data-testid="pickup-other-days">
              Ældre ordrer
            </p>
            {extraVisible.map((o) => (
              <PickupCard key={o.id} o={o} now={now} onAct={act} onCustomer={setCustId} highlight={hl.includes(o.id)} />
            ))}
          </>
        )}
        {list.length === 0 &&
          extraVisible.length === 0 &&
          (term ? (
            <p className="empty">Ingen match. Tjek koden – eller søg på navnet.</p>
          ) : (
            <div className="empty panel" data-testid="pickup-empty">
              <p>{tab === "udleveret" ? "Intet udleveret endnu i dag." : tab === "mangler" ? "Ingen udestående afhentninger." : "Ingen afhentninger de seneste 14 dage."}</p>
              {!store?.staff && (
                <Link className="btn" to="/overblik">
                  Til overblik
                </Link>
              )}
            </div>
          ))}
      </div>

      {scanning && <QrScanner onResult={onScan} onClose={() => setScanning(false)} shopName={shopName} />}
      {scanned && (
        <>
          <div className="sheet-scrim" onClick={() => setScanned(null)} />
          <div className="sheet" role="dialog" aria-label="Scannet ordre" data-testid="scanned-order">
            <div className="scanner-head">
              <div className="sh-title">
                <h2>Scannet ordre</h2>
                <small className="sh-shop" title={shopName} data-testid="scanned-shop">Butik: <b>{shopName}</b></small>
              </div>
              <button className="ib-close" aria-label="Luk" onClick={() => setScanned(null)} data-testid="scanned-close">
                <X size={20} />
              </button>
            </div>
            <PickupCard o={scanned} now={Date.now()} onAct={act} onCustomer={setCustId} big />
          </div>
        </>
      )}
      {custId && <CustomerPanel cid={custId} onClose={() => setCustId(null)} onChanged={load} shopName={shopName} />}
      {undo && <UndoToast u={undo} onUndo={revert} onDone={closeUndo} />}
    </>
  );
}
